/**
 * Messages component module for rendering chat conversation threads.
 *
 * This module provides the main Messages component that renders a scrollable
 * list of chat messages with support for streaming, followup questions,
 * and various interactive features.
 *
 * @module components/chat/messages/messages
 */
import { computeArtifactRenderModes } from "@/lib/artifacts/render-modes";
import { computeChipRenderPlans, isRealUserMessage } from "@/lib/attachments/auto-chip-plans";
import { ChatMessage, type ChatSurface } from "@/types/chat-message";
import type { UseChatHelpers } from "@ai-sdk/react";
import type { CompactionEvent } from "@taqat/ai";
import { Fragment, useEffect, useMemo, useRef } from "react";
import type { AgentCallStreamStats } from "./agent-call-stream-state";
import { CompactionDivider } from "./compaction-divider";
import { PreviewMessage } from "./message";
import { isToolPartType, toolNameOfPart } from "./tool-call-message/tool-part";

/**
 * Props for the Messages component.
 *
 * @property chatId - Unique identifier for the current chat conversation
 * @property status - Current streaming status from the AI SDK ("ready", "streaming", "submitted", "error")
 * @property messages - Array of chat messages to render in the conversation
 * @property setMessages - Function to update the messages array (from useChat hook)
 * @property regenerate - Function to regenerate an AI response (from useChat hook)
 * @property isReadonly - When true, disables all interactive features (editing, regenerating)
 * @property sandboxId - Optional sandbox identifier for tool execution context
 * @property isCompact - When true, renders messages in a compact layout with reduced spacing
 * @property textToSpeechEnabled - When true, enables text-to-speech button on assistant messages
 * @property voiceSettings - Optional voice configuration for text-to-speech (voice name, speed, pitch)
 */
export type MessagesProps = {
  /** Unique identifier for the current chat conversation */
  chatId: string;
  /** Current streaming status from the AI SDK */
  status: UseChatHelpers<ChatMessage>["status"];
  /** Which product surface is rendering; coding draws its steps differently */
  surface?: ChatSurface;
  /** Array of chat messages to render in the conversation */
  messages: ChatMessage[];
  /** Function to update the messages array (from useChat hook) */
  setMessages: UseChatHelpers<ChatMessage>["setMessages"];
  /** Function to regenerate an AI response (from useChat hook) */
  regenerate: UseChatHelpers<ChatMessage>["regenerate"];
  /** When true, disables all interactive features (editing, regenerating) */
  isReadonly: boolean;
  /** Optional sandbox identifier for tool execution context */
  sandboxId?: string;
  /** When true, renders messages in a compact layout with reduced spacing */
  isCompact?: boolean;
  /** When true, enables text-to-speech button on assistant messages */
  textToSpeechEnabled?: boolean;
  /** Optional voice configuration for text-to-speech */
  voiceSettings?: { voice?: string; speed?: number; pitch?: number };
  /** Accumulated streaming text per agent ID for live agent call previews */
  agentStreams?: Record<string, string>;
  /** Live display metrics per streamed agent ID */
  agentStreamStats?: Record<string, AgentCallStreamStats>;
  /** Finalized streamed text per agent ID, preserved after stream-end so cards keep showing the full reply */
  agentFinalTexts?: Record<string, string>;
  /**
   * Compaction events to surface as in-stream dividers. Combines events
   * persisted on the chat record with live events streamed via
   * `data-compaction` parts during the current turn. Each entry is rendered
   * after the message whose `id` matches `afterMessageId`.
   */
  compactions?: CompactionEvent[];
};

/**
 * Pure (non-memoized) implementation of the Messages component.
 *
 * Renders a scrollable conversation thread with the following features:
 * - Auto-scroll to bottom when new messages arrive (via Conversation wrapper)
 * - Individual message rendering with PreviewMessage component
 * - Thinking indicator during AI response streaming
 * - Followup question suggestions after conversation completes
 * - Scroll-to-bottom button when scrolled up
 *
 * The component uses the Conversation pattern from ai-elements which provides
 * smooth auto-scrolling behavior via the use-stick-to-bottom library.
 *
 * @param props - Component props (see MessagesProps)
 * @returns Rendered conversation thread with messages and controls
 */
function PureMessages({
  chatId,
  status,
  surface = "assistant",
  messages,
  setMessages,
  regenerate,
  isReadonly,
  sandboxId,
  isCompact = false,
  textToSpeechEnabled = false,
  voiceSettings,
  agentStreams,
  agentStreamStats,
  agentFinalTexts,
  compactions,
}: MessagesProps) {
  /** Tracks previous chat ID to detect conversation changes */
  const previousChatIdRef = useRef(chatId);


  /** Determines if user has sent a message and is awaiting response */
  const hasSentMessage = messages.length > 0 && status !== "ready";

  /**
   * Cross-message render plan for artifact tool parts. Demotes superseded
   * `create_artifact` cards to badges and collapses model-retry duplicates
   * within a single assistant message. Recomputed each render so streaming
   * updates immediately reflect in card → badge transitions.
   */
  const artifactRenderPlans = useMemo(
    () => computeArtifactRenderModes(messages),
    [messages],
  );

  /**
   * Per-turn auto-chip plans. Aggregates sandbox/workspace file writes across
   * every assistant message in a turn and assigns the chip list to the last
   * assistant message. Lets chips render under the agent's text reply rather
   * than above the earlier tool-call messages, and only after that final
   * message finishes streaming.
   */
  const chipRenderPlans = useMemo(
    () => computeChipRenderPlans(messages),
    [messages],
  );

  /**
   * Map of message ID → compaction events anchored to that message. Built
   * once per render so the per-message lookup in the `messages.map` below is
   * O(1) instead of scanning the full list. We dedupe by event `id` so a
   * persisted event and a live-streamed copy of the same event do not render
   * twice during the brief window where both arrive.
   *
   * Anchoring strategy:
   *  1. Match `afterMessageId` directly (preferred — works for chats whose
   *     user messages are saved with their UIMessage id intact).
   *  2. If the id isn't found among the rendered messages, fall back to
   *     time-based anchoring: pick the latest rendered message whose
   *     createdAt is <= the event's createdAt. This keeps dividers visible
   *     after page reload even for older chats whose persisted
   *     afterMessageIds don't match any DB message id.
   */
  /**
   * Index every tool-call id → the id of the message that contains it, so a
   * compaction's `afterToolCallId` can be resolved to the message it should
   * render inside. Built once per render.
   */
  const toolCallIdToMessageId = useMemo(() => {
    const map = new Map<string, string>();
    for (const m of messages) {
      const parts = (m as { parts?: Array<{ toolCallId?: string }> }).parts;
      if (!Array.isArray(parts)) continue;
      for (const part of parts) {
        if (part?.toolCallId) map.set(part.toolCallId, m.id);
      }
    }
    return map;
  }, [messages]);

  /**
   * Compaction events that resolve to a tool card in a specific message, keyed
   * by that message id. These render INLINE (via PreviewMessage's `compactions`
   * prop) right after the tool card, matching the live-stream position. Events
   * whose `afterToolCallId` matches nothing fall through to the anchored
   * after-message rendering below.
   */
  const inlineCompactionsByMessageId = useMemo(() => {
    const map = new Map<string, CompactionEvent[]>();
    if (!compactions || compactions.length === 0) return map;
    const seen = new Set<string>();
    for (const event of compactions) {
      if (seen.has(event.id)) continue;
      const anchor = event.afterToolCallId;
      const messageId = anchor ? toolCallIdToMessageId.get(anchor) : undefined;
      if (!messageId) continue;
      seen.add(event.id);
      const existing = map.get(messageId);
      if (existing) existing.push(event);
      else map.set(messageId, [event]);
    }
    return map;
  }, [compactions, toolCallIdToMessageId]);

  const compactionsByMessageId = useMemo(() => {
    const map = new Map<string, CompactionEvent[]>();
    if (!compactions || compactions.length === 0) return map;

    const messageIdSet = new Set(messages.map((m) => m.id));
    // Index of (createdAt timestamp, message id) sorted ascending so we can
    // resolve the time-based fallback in a single linear walk per event.
    // The DB record exposes `createdAt` at the top level; the typed
    // MessageMetadata also carries one. Prefer whichever is present.
    const readCreatedAt = (m: ChatMessage): number => {
      const top = (m as unknown as { createdAt?: string | Date }).createdAt;
      const metaTime = (m.metadata as { createdAt?: string | Date } | undefined)
        ?.createdAt;
      const value = top ?? metaTime;
      return value ? new Date(value as string).getTime() : NaN;
    };
    const messagesByTime = messages
      .map((m, index) => ({ id: m.id, index, createdAt: readCreatedAt(m) }))
      .filter((m) => !Number.isNaN(m.createdAt))
      .sort((a, b) => a.createdAt - b.createdAt);

    const seen = new Set<string>();
    for (const event of compactions) {
      if (seen.has(event.id)) continue;
      seen.add(event.id);

      // Skip events already rendered inline (their tool card lives in a
      // message) so they aren't duplicated as an after-message divider.
      if (event.afterToolCallId && toolCallIdToMessageId.has(event.afterToolCallId)) {
        continue;
      }

      let anchorId: string | undefined;
      if (event.afterMessageId && messageIdSet.has(event.afterMessageId)) {
        anchorId = event.afterMessageId;
      } else {
        const eventTime = new Date(event.createdAt).getTime();
        // Walk in ascending createdAt order; the last entry whose timestamp
        // is <= eventTime is the anchor we want.
        let best: string | undefined;
        for (const m of messagesByTime) {
          if (m.createdAt > eventTime) break;
          best = m.id;
        }
        anchorId = best;
      }

      if (!anchorId) continue;
      const existing = map.get(anchorId);
      if (existing) existing.push(event);
      else map.set(anchorId, [event]);
    }
    return map;
  }, [compactions, messages, toolCallIdToMessageId]);

  /**
   * Effect to track chat ID changes for scroll position management.
   * Updates the ref when navigating between different conversations.
   */
  useEffect(() => {
    if (chatId !== previousChatIdRef.current) {
      previousChatIdRef.current = chatId;
    }
  }, [chatId]);

  /**
   * Merge tool outputs back onto the originating assistant tool-call part.
   *
   * For tools that suspend on the client (e.g. `ask_user_question`), the AI
   * SDK persists the call in two pieces: the assistant message carries the
   * tool part with `state: 'input-available'` (no output), and the matching
   * `addToolResult` lands in a follow-up synthetic user/tool message with
   * `state: 'output-available'`. That synthetic message is hidden by
   * `isRealUserMessage` below — which means renderers that read `output`
   * from the assistant part see nothing.
   *
   * We index every `output-available` tool part by `toolCallId`, then patch
   * any earlier assistant `input-available` tool part with the same id so
   * downstream renderers see the resolved state. Idempotent — no-op when
   * the assistant part already has its own output.
   */
  const messagesWithMergedToolOutputs = useMemo(() => {
    const outputs = new Map<string, unknown>();
    for (const m of messages) {
      for (const part of m.parts) {
        const t = part.type as string;
        if (!isToolPartType(t)) continue;
        const p = part as { state?: string; toolCallId?: string; output?: unknown };
        if (p.state === "output-available" && p.toolCallId && p.output !== undefined) {
          outputs.set(p.toolCallId, p.output);
        }
      }
    }
    if (outputs.size === 0) return messages;
    return messages.map((m) => {
      if (m.role !== "assistant") return m;
      let changed = false;
      const nextParts = m.parts.map((part) => {
        const t = part.type as string;
        if (!isToolPartType(t)) return part;
        const p = part as { state?: string; toolCallId?: string; output?: unknown };
        if (p.state === "output-available") return part;
        if (!p.toolCallId || !outputs.has(p.toolCallId)) return part;
        changed = true;
        return {
          ...(part as Record<string, unknown>),
          state: "output-available",
          output: outputs.get(p.toolCallId),
        } as typeof part;
      });
      return changed ? { ...m, parts: nextParts } : m;
    });
  }, [messages]);

  /**
   * Filter out synthetic role=user messages the AI SDK interleaves between
   * assistant turns to relay state (data-status / step-start / reasoning /
   * tool parts) — they carry no real prompt text. Rendering them paints the
   * relayed reasoning + tool calls inside a user-aligned bubble (right side
   * of the conversation), which looks like a duplicate of the assistant's
   * own work. We keep them out of the render list while still letting them
   * exist in the underlying messages array.
   */
  const filteredMessages = useMemo(
    () =>
      messagesWithMergedToolOutputs.filter(
        (m) =>
          (m.role !== "user" || isRealUserMessage(m)) &&
          // Trigger messages flagged as hidden (system-sent, not user-typed)
          // "Approved. Execute the plan now." prompt) are kept in the
          // underlying messages array so the AI SDK can stream against
          // them, but excluded from the visual transcript — the plan card's
          // "Approved" badge already communicates the state.
          (m as { metadata?: { hidden?: boolean } }).metadata?.hidden !== true,
      ),
    [messagesWithMergedToolOutputs],
  );

  /**
   * Drop assistant messages whose visible content is a prefix of the next
   * assistant message's content. During multi-step streaming the AI SDK's
   * client state holds *cumulative* snapshots — at step N+1 a fresh
   * assistant message arrives with all of step N's parts plus the new ones.
   * Rendering every snapshot stacks the same reasoning / tool calls multiple
   * times under the user's prompt. Once the chat reloads, the persisted form
   * is one non-cumulative message per step (no prefix relation), so this
   * filter becomes a no-op for loaded chats.
   *
   * Fingerprint considers only user-visible parts (text / reasoning / tool
   * calls / step-start) so that incidental noise like `data-status` /
   * `data-title` frames don't break the prefix match.
   *
   * "Tool calls" must include the `dynamic-tool` shape. A coding step is often
   * nothing BUT tool calls, so excluding them fingerprints the whole message as
   * empty — and an empty fingerprint before a non-empty one is dropped as a
   * superseded snapshot, taking every tool call in it off the transcript.
   */
  const visibleMessages = useMemo(() => {
    const fingerprint = (m: ChatMessage): string =>
      m.parts
        .filter((p) => {
          const t = p.type as string;
          return (
            t === "text" ||
            t === "text-delta" ||
            t === "reasoning" ||
            t === "reasoning-delta" ||
            t === "step-start" ||
            isToolPartType(t)
          );
        })
        .map((p) => {
          const t = p.type as string;
          if (t === "text" || t === "text-delta") {
            return `t:${(p as { text?: string }).text ?? ""}`;
          }
          if (t === "reasoning" || t === "reasoning-delta") {
            return `r:${
              (p as { text?: string; reasoning?: string }).text ??
              (p as { reasoning?: string }).reasoning ??
              ""
            }`;
          }
          if (isToolPartType(t)) {
            const id = (p as { toolCallId?: string }).toolCallId ?? "";
            // The name is appended because every dynamic tool shares one type,
            // so `t` alone does not distinguish two different calls.
            return `${t}:${toolNameOfPart(p as { type: string; toolName?: string }) ?? ""}:${id}`;
          }
          return t;
        })
        .join("|");

    const result: ChatMessage[] = [];
    for (let i = 0; i < filteredMessages.length; i++) {
      const m = filteredMessages[i];
      if (m.role === "assistant" && i + 1 < filteredMessages.length) {
        const next = filteredMessages[i + 1];
        if (next.role === "assistant") {
          const fpCurrent = fingerprint(m);
          const fpNext = fingerprint(next);
          if (fpCurrent.length === 0 && fpNext.length > 0) continue;
          if (fpCurrent.length > 0 && fpNext.startsWith(fpCurrent)) continue;
        }
      }
      result.push(m);
    }
    return result;
  }, [filteredMessages]);

  /**
   * Set of message IDs that are the final assistant message of their turn —
   * the "last assistant before the next real user prompt or end of list."
   * Used to suppress copy/regenerate footers on intermediate multi-step
   * assistant messages (reasoning-only / tool-call-only / interim text)
   * which are part of the same logical reply and shouldn't look like
   * separate, individually-regeneratable answers.
   */
  const lastAssistantOfTurnIds = useMemo(() => {
    const ids = new Set<string>();
    let pending: string | null = null;
    for (const m of visibleMessages) {
      if (m.role === "user") {
        if (pending) ids.add(pending);
        pending = null;
      } else if (m.role === "assistant") {
        pending = m.id;
      }
    }
    if (pending) ids.add(pending);
    return ids;
  }, [visibleMessages]);

  return (
    <>
      {visibleMessages.map((message, index) => {
        // For assistant messages, find next user message text for form value restoration
        let nextUserMessageText: string | undefined;
        if (message.role === 'assistant' && index < visibleMessages.length - 1) {
          const nextMsg = visibleMessages[index + 1];
          if (nextMsg?.role === 'user') {
            const textPart = nextMsg.parts?.find((p) => p.type === 'text');
            nextUserMessageText = (textPart as { type: 'text'; text: string })?.text;
          }
        }

        const isLast = visibleMessages.length - 1 === index;
        const compactionsAfter = compactionsByMessageId.get(message.id);
        const inlineCompactions = inlineCompactionsByMessageId.get(message.id);

        return (
          <Fragment key={message.id}>
            <PreviewMessage
              sandboxId={sandboxId}
              surface={surface}
              message={message}
              isLoading={status === "streaming" && isLast}
              isLastMessage={isLast}
              setMessages={setMessages}
              regenerate={regenerate}
              isReadonly={isReadonly}
              requiresScrollPadding={hasSentMessage && isLast}
              isCompact={isCompact}
              textToSpeechEnabled={textToSpeechEnabled}
              voiceSettings={voiceSettings}
              nextUserMessageText={nextUserMessageText}
              agentStreams={status === "streaming" && isLast ? agentStreams : undefined}
              agentStreamStats={agentStreamStats}
              agentFinalTexts={agentFinalTexts}
              artifactRenderPlan={artifactRenderPlans.get(message.id)}
              autoChipFiles={chipRenderPlans.get(message.id)}
              isLastAssistantOfTurn={lastAssistantOfTurnIds.has(message.id)}
              compactions={inlineCompactions}
            />
            {compactionsAfter?.map((event) => (
              <CompactionDivider
                key={event.id}
                messageCount={event.messageCount}
                createdAt={event.createdAt}
                summary={event.summary}
              />
            ))}
          </Fragment>
        );
      })}
    </>
  );
}

export const Messages = PureMessages;
